import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Shield, Upload, RefreshCcw, BarChart3, Film, Tv, Users, Clock, Star, X, Radio, Award, User2, Check, Ban, Search as SearchIcon, KeyRound, Mail, Crown, Clapperboard, Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Modal, FileField, EMPTY_EPISODE } from "./shared";

/* ---------------- Episodes ---------------- */
export default function Episodes() {
  const [series, setSeries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [eps, setEps] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(EMPTY_EPISODE);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    api.get("/admin/videos").then(r => setSeries(r.data.filter(v => v.type === "series")));
  }, []);

  const loadEps = (id) => api.get(`/videos/${id}/episodes`).then(r => setEps(r.data));
  useEffect(() => { if (selected) loadEps(selected.id); }, [selected]);

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const field = kind === "video" ? "stream_url" : "thumbnail";
      setForm((f) => ({ ...f, [field]: data.url }));
      toast.success(`${kind} uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading((u) => ({ ...u, [kind]: false })); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        season: parseInt(form.season),
        episode_number: parseInt(form.episode_number),
        duration_min: parseInt(form.duration_min),
      };
      await api.post(`/admin/videos/${selected.id}/episodes`, payload);
      toast.success("Episode added");
      setShow(false); setForm(EMPTY_EPISODE); loadEps(selected.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (epId) => {
    if (!confirm("Delete this episode?")) return;
    await api.delete(`/admin/videos/${selected.id}/episodes/${epId}`);
    loadEps(selected.id); toast.success("Deleted");
  };

  // group by season
  const seasons = {};
  eps.forEach(e => { (seasons[e.season] ||= []).push(e); });

  return (
    <div className="grid md:grid-cols-3 gap-6">
      {/* Series list */}
      <aside className="md:col-span-1 bg-[#0f0f0f] border border-white/10 rounded-lg p-3 max-h-[70vh] overflow-y-auto">
        <h3 className="text-xs uppercase tracking-widest text-gray-500 px-2 mb-2">Series ({series.length})</h3>
        {series.length === 0 && <p className="text-gray-500 text-sm p-3">No series in catalog. Add one in Catalog tab.</p>}
        {series.map(s => (
          <button
            key={s.id}
            data-testid={`series-${s.id}`}
            onClick={() => setSelected(s)}
            className={`w-full text-left p-2 rounded flex items-center gap-3 mb-1 transition-colors ${selected?.id === s.id ? "bg-[#E50914]/20 border border-[#E50914]/40" : "hover:bg-white/5"}`}
          >
            <img src={s.poster} alt="" className="w-9 h-12 object-cover rounded" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold line-clamp-1">{s.title}</div>
              <div className="text-xs text-gray-500">{s.year}</div>
            </div>
          </button>
        ))}
      </aside>

      {/* Episodes panel */}
      <section className="md:col-span-2">
        {!selected ? (
          <div className="bg-[#0f0f0f] border border-white/10 rounded-lg p-12 text-center text-gray-500">
            ← Select a series to manage its episodes
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{selected.title} — Episodes ({eps.length})</h3>
              <button data-testid="add-episode-btn" onClick={() => setShow(true)} className="btn-red px-4 py-2 rounded font-semibold flex items-center gap-2 text-sm"><Plus className="w-4 h-4" /> Add Episode</button>
            </div>
            {Object.keys(seasons).sort((a, b) => +a - +b).map(season => (
              <div key={season} className="mb-6">
                <h4 className="text-sm uppercase tracking-widest text-gray-500 mb-2">Season {season}</h4>
                <div className="space-y-2">
                  {seasons[season].sort((a,b) => a.episode_number - b.episode_number).map(ep => (
                    <div key={ep.episode_id} data-testid={`ep-${ep.episode_id}`} className="bg-[#141414] border border-white/5 rounded p-3 flex items-center gap-4">
                      {ep.thumbnail ? <img src={ep.thumbnail} alt="" className="w-20 h-12 object-cover rounded" /> : <div className="w-20 h-12 bg-[#1a1a1a] rounded flex items-center justify-center text-xs text-gray-600">E{ep.episode_number}</div>}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">E{ep.episode_number}. {ep.title}</div>
                        <div className="text-xs text-gray-400 line-clamp-1">{ep.description}</div>
                        <div className="text-xs text-gray-500 mt-1">{ep.duration_min} min{ep.stream_url ? " · video uploaded" : " · no video"}</div>
                      </div>
                      <button onClick={() => del(ep.episode_id)} className="text-red-500 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {eps.length === 0 && <p className="text-gray-500 text-sm p-6 bg-[#0f0f0f] border border-white/10 rounded text-center">No episodes yet.</p>}
          </>
        )}
      </section>

      {show && selected && (
        <Modal onClose={() => setShow(false)} title={`Add episode to ${selected.title}`}>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <input type="number" min={1} required placeholder="Season" className="input-dark" value={form.season} onChange={e => setForm({...form, season: e.target.value})} />
            <input type="number" min={1} required placeholder="Episode number" className="input-dark" value={form.episode_number} onChange={e => setForm({...form, episode_number: e.target.value})} />
            <input data-testid="ep-title" required placeholder="Episode title" className="input-dark col-span-2" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            <input type="number" placeholder="Duration min" className="input-dark col-span-2" value={form.duration_min} onChange={e => setForm({...form, duration_min: e.target.value})} />
            <FileField label="Thumbnail" url={form.thumbnail} onUrl={(u) => setForm({...form, thumbnail: u})} loading={uploading.thumbnail} onFile={(f) => upload("thumbnail", f)} accept="image/*" />
            <FileField label="Video file (mp4)" url={form.stream_url} onUrl={(u) => setForm({...form, stream_url: u})} loading={uploading.video} onFile={(f) => upload("video", f)} accept="video/*" testid="ep-upload-video" />
            <textarea placeholder="Episode description" rows={3} className="input-dark col-span-2" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            <div className="col-span-2 flex gap-3 pt-2">
              <button data-testid="ep-save" type="submit" className="btn-red flex-1 py-3 rounded font-semibold">Save Episode</button>
              <button type="button" onClick={() => setShow(false)} className="btn-glass flex-1 py-3 rounded font-semibold">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
